class QuestionService {
  constructor() {
    this.questionCache = new Map();
  }

  async loadQuestions(quizConfig) {
    if (quizConfig.quizJson) {
      // Direct question set (e.g., test quiz)
      return this.loadDirectQuestionSet(quizConfig.quizJson);
    } else if (quizConfig.quizTopics) {
      // Dynamic question set (e.g., math-1 quiz)
      return this.loadDynamicQuestionSet(quizConfig.quizTopics);
    }
    return [];
  }

  async loadDirectQuestionSet(quizJsonPath) {
    if (this.questionCache.has(quizJsonPath)) {
      return this.questionCache.get(quizJsonPath);
    }
    
    try {
      // Remove any leading slashes to prevent double slashes
      const cleanPath = quizJsonPath.replace(/^\/+/, '');
      const response = await fetch(`/${cleanPath}`);
      
      if (!response.ok) {
        throw new Error(`Failed to load question set: ${response.statusText}`);
      }
      
      const data = await response.json();
      const questions = Array.isArray(data) ? data : data.questions || [];
      
      if (!questions.length) {
        console.warn(`No questions found in ${quizJsonPath}`);
      }
      
      this.questionCache.set(quizJsonPath, questions);
      return questions;
    } catch (error) {
      console.error('Error loading question set:', error);
      throw new Error(`Failed to load question set: ${error.message}`);
    }
  }

  async loadDynamicQuestionSet(quizTopics) {
    const questionsByTopic = [];
    
    // First, collect all questions grouped by topic
    for (const topic of quizTopics) {
      try {
        // Load questions from topics/{topic-name}/index.json
        let topicQuestions = await this.loadQuestionsByTopic(topic.topic);
        
        // Filter by complexity if specified (handles '*' as no filter)
        topicQuestions = this.filterQuestionsByComplexity(
          topicQuestions,
          topic.complexity
        );
        
        // Select random questions for this topic, ensuring no duplicates within the topic
        const selectedQuestions = [];
        const availableIndices = Array.from({ length: topicQuestions.length }, (_, i) => i);
        
        // Shuffle the indices to get random selection
        for (let i = availableIndices.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [availableIndices[i], availableIndices[j]] = [availableIndices[j], availableIndices[i]];
        }
        
        // Take the first N unique questions
        const numQuestions = Math.min(topic.numberOfQuestions, availableIndices.length);
        for (let i = 0; i < numQuestions; i++) {
          const questionIndex = availableIndices[i];
          selectedQuestions.push(topicQuestions[questionIndex]);
        }
        
        if (selectedQuestions.length > 0) {
          questionsByTopic.push({
            topic: topic.topic,
            questions: selectedQuestions
          });
        }
      } catch (error) {
        console.error(`Error loading questions for topic ${topic.topic}:`, error);
      }
    }
    
    // Interleave questions from different topics to avoid grouping
    const interleavedQuestions = this.interleaveQuestions(questionsByTopic);
    
    return interleavedQuestions;
  }

  async loadQuestionsByTopic(topic) {
    // Convert topic to path (e.g., "Number Sense" -> "number-sense")
    const topicPath = topic.toLowerCase().replace(/\s+/g, '-');
    const url = `/quiz-data/topics/${topicPath}/index.json`;
    
    try {
      const response = await fetch(url);
      const data = await response.json();
      return Array.isArray(data) ? data : data.questions || [];
    } catch (error) {
      console.error(`Error loading questions for topic ${topic}:`, error);
      return [];
    }
  }

  filterQuestionsByComplexity(questions, complexity) {
    if (!complexity || complexity === '*') return questions;
    return questions.filter(q => q.complexity === complexity);
  }

  selectRandomQuestions(questions, count) {
    if (!questions?.length) return [];
    const shuffled = [...questions].sort(() => 0.5 - Math.random());
    return shuffled.slice(0, Math.min(count, questions.length));
  }
  
  /**
   * Interleaves questions from different topics to avoid grouping
   * @param {Array} questionsByTopic Array of { topic: string, questions: Array }
   * @returns {Array} Interleaved questions
   */
  interleaveQuestions(questionsByTopic) {
    // First, flatten all questions into a single array with topic info
    const allQuestions = [];
    
    // Create a copy of the questionsByTopic array to avoid mutating the original
    const topics = questionsByTopic.map(topic => ({
      ...topic,
      questions: [...topic.questions],
      index: 0
    }));
    
    // Continue until all questions are processed
    let remainingTopics = topics.filter(topic => topic.questions.length > 0);
    
    while (remainingTopics.length > 0) {
      // Shuffle the remaining topics to ensure randomness in interleaving
      remainingTopics = remainingTopics.sort(() => 0.5 - Math.random());
      
      // Take one question from each topic
      for (const topic of remainingTopics) {
        if (topic.questions.length > 0) {
          allQuestions.push(topic.questions.shift());
        }
      }
      
      // Update remaining topics
      remainingTopics = remainingTopics.filter(topic => topic.questions.length > 0);
    }
    
    return allQuestions;
  }
}

export const questionService = new QuestionService();
